/**
 * Multi-game client-side router using History API
 *
 * Routes are prefixed by gameType for game-scoped screens.
 * Shared routes (login, register, home, guest-login, guest-join) have no prefix.
 */

import { useSyncExternalStore } from "react";
import { isValidGameType, normalizeGameType } from "./game-registry";

export type RouteMode = "legacy" | "v2";

export type Route =
  | { name: "home" }
  | { name: "login" }
  | { name: "guestLogin" }
  | { name: "register" }
  | { name: "lobby"; gameType: string; routeMode?: RouteMode }
  | {
      name: "guestJoin";
      gameType: string;
      roomNumber: string;
      secret: string;
      seat: "player" | "observer";
    }
  | {
      name: "game";
      gameType: string;
      gameId: string;
      routeMode?: RouteMode;
    }
  | {
      name: "gameRoom";
      gameType: string;
      gameId: string;
      routeMode?: RouteMode;
    }
  | {
      name: "gameReplay";
      gameType: string;
      scenario: string;
      routeMode?: RouteMode;
    }
  | { name: "replayLobby" }
  | { name: "replayRoom"; shareCode: string }
  | { name: "notFound"; path: string };

const listeners = new Set<() => void>();
let currentRoute: Route = parseRoute(getPathWithSearch());

function getPathWithSearch(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  return `${window.location.pathname}${window.location.search}`;
}

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function normalizePath(path: string): string {
  if (!path.startsWith("/")) {
    return `/${path}`;
  }
  return path;
}

function getRouteModeForGameType(gameType: string): RouteMode {
  void gameType;
  return "v2";
}

export function parseRoute(pathWithSearch: string): Route {
  const normalized = normalizePath(pathWithSearch);
  const url = new URL(normalized, "http://localhost");
  const path = url.pathname;

  if (path === "/") {
    return { name: "home" };
  }
  if (path === "/login") {
    return { name: "login" };
  }
  if (path === "/guest-login") {
    return { name: "guestLogin" };
  }
  if (path === "/register") {
    return { name: "register" };
  }
  if (path === "/guest-join") {
    return {
      name: "guestJoin",
      gameType: url.searchParams.get("gameType") ?? "",
      roomNumber: url.searchParams.get("roomNumber") ?? "",
      secret: url.searchParams.get("secret") ?? "",
      seat: url.searchParams.get("seat") === "observer" ? "observer" : "player",
    };
  }
  // Replay rooms — share-code driven, game-agnostic, backend-free to render.
  if (path === "/replay/lobby") {
    return { name: "replayLobby" };
  }
  if (path === "/replay/room") {
    return {
      name: "replayRoom",
      shareCode: url.searchParams.get("code") ?? "",
    };
  }

  const segments = path.split("/").filter(Boolean);

  // Game-type prefixed routes: /{gameType}/...
  if (segments.length >= 1 && isValidGameType(segments[0])) {
    const gameType = normalizeGameType(segments[0]);

    // /{gameType}/lobby
    if (segments.length === 2 && segments[1] === "lobby") {
      return {
        name: "lobby",
        gameType,
        routeMode: getRouteModeForGameType(gameType),
      };
    }

    // /{gameType}/replay/:scenario
    if (segments.length === 3 && segments[1] === "replay") {
      return {
        name: "gameReplay",
        gameType,
        scenario: segments[2],
        routeMode: getRouteModeForGameType(gameType),
      };
    }

    // /{gameType}/games/:id/room
    if (
      segments.length === 4 &&
      segments[1] === "games" &&
      segments[3] === "room"
    ) {
      return {
        name: "gameRoom",
        gameType,
        gameId: segments[2],
        routeMode: getRouteModeForGameType(gameType),
      };
    }

    // /{gameType}/games/:id
    if (segments.length === 3 && segments[1] === "games") {
      return {
        name: "game",
        gameType,
        gameId: segments[2],
        routeMode: getRouteModeForGameType(gameType),
      };
    }
  }

  return { name: "notFound", path };
}

function setRoute(nextRoute: Route): void {
  currentRoute = nextRoute;
  notify();
}

export function navigateTo(
  path: string,
  options?: { replace?: boolean },
): void {
  if (typeof window === "undefined") return;

  const nextPath = normalizePath(path);
  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (currentPath === nextPath) return;

  if (options?.replace) {
    window.history.replaceState(null, "", nextPath);
  } else {
    window.history.pushState(null, "", nextPath);
  }
  setRoute(parseRoute(nextPath));
}

export function buildLobbyPath(
  gameType: string,
  _routeMode: RouteMode = "v2",
): string {
  return `/${gameType}/lobby`;
}

export function buildGamePath(
  gameType: string,
  gameId: string,
  _routeMode: RouteMode = "v2",
): string {
  return `/${gameType}/games/${gameId}`;
}

export function buildGameRoomPath(
  gameType: string,
  gameId: string,
  _routeMode: RouteMode = "v2",
): string {
  return `/${gameType}/games/${gameId}/room`;
}

export function buildGameReplayPath(
  gameType: string,
  scenario: string,
  _routeMode: RouteMode = "v2",
): string {
  return `/${gameType}/replay/${scenario}`;
}

export function buildReplayLobbyPath(): string {
  return "/replay/lobby";
}

export function buildReplayRoomPath(shareCode: string): string {
  const query = new URLSearchParams({ code: shareCode });
  return `/replay/room?${query.toString()}`;
}

export function buildGuestJoinPath(
  gameType: string,
  roomNumber: string,
  secret: string,
  seat: "player" | "observer" = "player",
): string {
  const query = new URLSearchParams({ gameType, roomNumber, secret });
  if (seat === "observer") {
    query.set("seat", "observer");
  }
  return `/guest-join?${query.toString()}`;
}

export function useRoute(): Route {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => currentRoute,
  );
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    setRoute(
      parseRoute(`${window.location.pathname}${window.location.search}`),
    );
  });
}
